/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreTimeFormat - kTimeCore's ISO and localised text formatting.
 * Split out of kCoreTime.ts (2026-10-19, owner: smaller files for the reuse reviewer). The bodies are
 * moved VERBATIM except that `this` is the explicit first parameter `Self` (private/protected members are
 * read as Self["Name"]); kTimeCore keeps every member as a same-signature delegating member.
 */

import { getCoreUtils, serverLocale } from "./kCoreHandler";
import type { kTimeCore, IRelativeTimeFormatCtor, IIntlWithRelativeTime } from "./kCoreTime";

export class kCoreTimeFormat {
    /** "yyyy-MM-dd" — empty string when year ≤ 1971. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.ToISODate - its gate, try/catch and handler label "kTimeCore.ToISODate" stay verbatim, so error reports are unchanged.
    public static ToISODate = (Self: kTimeCore): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self.Year > 1971) {
                return Self.Year
                    + "-" + ("0" + Self.Month.toString()).slice(-2)
                    + "-" + ("0" + Self.Day.toString()).slice(-2);
            }
            return "";
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.ToISODate"); return ""; }
    };

    /** "HH:mm" substring of ToISODateTime. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.ToISOTime - its gate, try/catch and handler label "kTimeCore.ToISOTime" stay verbatim, so error reports are unchanged.
    public static ToISOTime = (Self: kTimeCore): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self.Year > 1971) { return Self.ToISODateTime().substring(11, 16); }
            return "";
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.ToISOTime"); return ""; }
    };

    /**
     * "yyyy-MM-dd HH:mm" — EMPTY STRING when the date is unset, matching ToISODate and ToISOZuluDate.
     *
     * The empty case is load-bearing, not tidiness. This used to append the time unconditionally, so
     * an unset date (ToISODate → "") produced " 00:00" — which is not a date any SharePoint DateTime
     * column will accept, and SharePoint validates a list item as a WHOLE, so one such field rejects
     * the ENTIRE write rather than just that column. Any entity with an OPTIONAL date field (e.g.
     * SecureArea.LabelPublishedOn) therefore broke every write to its list the moment the date was
     * legitimately not set yet. Returning "" clears the column, which is what "no value" means.
     */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.ToISODateTime - its gate, try/catch and handler label "kTimeCore.ToISODateTime" stay verbatim, so error reports are unchanged.
    public static ToISODateTime = (Self: kTimeCore): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const DatePart: string = Self.ToISODate();
            if (DatePart === "") { return ""; }
            return DatePart
                + " " + ("0" + Self.Hour.toString()).slice(-2)
                + ":" + ("0" + Self.Minute.toString()).slice(-2);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.ToISODateTime"); return ""; }
    };

    /** "yyyy-MM-ddT00:00:00Z" — the date at UTC midnight, the form SharePoint wants for
     *  a date-only Events value (recurrence windowEnd, an all-day EventDate). Built by
     *  concatenation rather than `new Date(v).toISOString()` on purpose: the Date
     *  round-trip re-interprets the value against the browser's zone and can slide the
     *  day by one either side of UTC. Empty string when the date is unset (year <= 1971),
     *  matching ToISODate. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.ToISOZuluDate - its gate, try/catch and handler label "kTimeCore.ToISOZuluDate" stay verbatim, so error reports are unchanged.
    public static ToISOZuluDate = (Self: kTimeCore): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const DatePart: string = Self.ToISODate();
            if (DatePart === "") { return ""; }
            return DatePart + "T00:00:00Z";
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.ToISOZuluDate"); return ""; }
    };

    /**
     * "yyyy-MM-ddTHH:mm:ss.sssZ" — the true UTC INSTANT of this value, or "" when unset.
     *
     * The right thing to send SharePoint for a DateTime column. RUNTIME-MEASURED against a live list
     * 2026-08-05 (odata=verbose, the dialect kSPRestCore uses): writing ToISODateTime()'s
     * "2026-08-05 09:21" is ACCEPTED but read back as 16:21 — SharePoint takes a zone-less string as
     * LOCAL and converts, so the stored instant is wrong by the offset. This form read back 09:21:00
     * exactly. (Under odata=nometadata the zone-less form is REJECTED outright, so it is not merely
     * imprecise, it is dialect-dependent.)
     *
     * NOT TreatAsUTC(): that SHIFTS the components to pretend they were already UTC. This converts the
     * instant, which is what a timestamp means.
     */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.ToISOZuluDateTime - its gate, try/catch and handler label "kTimeCore.ToISOZuluDateTime" stay verbatim, so error reports are unchanged.
    public static ToISOZuluDateTime = (Self: kTimeCore): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self.Year <= 1971) { return ""; }
            return Self.ToJsDate().toISOString();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.ToISOZuluDateTime"); return ""; }
    };

    /** "yyyy-MM-dd HH:mm ss" (adds seconds) — empty string when the date is unset, as ToISODateTime. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.ToISODateTimeSecs - its gate, try/catch and handler label "kTimeCore.ToISODateTimeSecs" stay verbatim, so error reports are unchanged.
    public static ToISODateTimeSecs = (Self: kTimeCore): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const Stamp: string = Self.ToISODateTime();
            if (Stamp === "") { return ""; }
            return Stamp + " " + ("0" + Self.Second.toString()).slice(-2);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.ToISODateTimeSecs"); return ""; }
    };

    // ── Locale-aware formatting ───────────────────────────────────────────────

    // The short-date replacement spine shared by kTimeCore.ToLocalShortDate and kTime's
    // browser override (findings 6871/7845): yyyy/mm/m/dd/d substitutions over the
    // lower-cased pattern; the pattern's source is the caller's only difference.
    // DOCUMENTED EXCEPTION: pure string formatting under the caller's own gate+try and
    // per-member label.
    public static _FormatShortDate = (Self: kTimeCore, Fmt: string): string => {
        Fmt = Fmt.replace("yyyy", Self.Year.toString());
        Fmt = Fmt.replace("mm", ("0" + Self.Month.toString()).slice(-2));
        Fmt = Fmt.replace("m", Self.Month.toString());
        Fmt = Fmt.replace("dd", ("0" + Self.Day.toString()).slice(-2));
        Fmt = Fmt.replace("d", Self.Day.toString());
        return Fmt;
    };

    /**
     * Formats the date using kTime.DateFormat (default "yyyy-MM-dd").
     * Set kTime.DateFormat = "dd/MM/yyyy" once at startup for regional formatting.
     */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.ToLocalShortDate - its gate, try/catch and handler label "kTimeCore.ToLocalShortDate" stay verbatim, so error reports are unchanged.
    public static ToLocalShortDate = (Self: kTimeCore, DateFormat: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self.Year <= 1970) { return ""; }
            return Self["_FormatShortDate"](DateFormat.toLowerCase());
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.ToLocalShortDate"); return ""; }
    };

    /** "dd/MM/yyyy HH:mm" (or local format) + locale time string. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.ToLocalShortDateAndTime - its gate, try/catch and handler label "kTimeCore.ToLocalShortDateAndTime" stay verbatim, so error reports are unchanged.
    public static ToLocalShortDateAndTime = (Self: kTimeCore): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self.Year <= 1969) { return ""; }
            return Self.ToLocalShortDate() + " "
                + Self.ToJsDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.ToLocalShortDateAndTime"); return ""; }
    };

    /**
     * Compact, context-aware local date — the "Facebook" practice of dropping the
     * parts the reader can infer, to remove clutter:
     *   • today              → time only            (e.g. "14:30")
     *   • this year, midnight→ month + day, no year (e.g. "30 Jun")
     *   • this year, w/ time → month + day + time   (e.g. "30 Jun 14:30")
     *   • earlier, midnight  → full date with year  (e.g. "30 Jun 2024")
     *   • earlier, w/ time   → full date + time     (e.g. "30 Jun 2024 14:30")
     * On a non-today date the local HH:mm is appended only when the time is NOT
     * midnight (00:00) — so a bare date stays a bare date. The output is
     * locale-formatted (month order + time pattern follow the locale). Pass a
     * BCP-47 tag ("sv-SE", "de-DE", "en-GB") to force a locale; leave Locale empty
     * to use the host locale (serverLocale()). The browser kTime subclass overrides
     * the empty default to navigator.languages[0].
     */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.ToSmartLocalDate - its gate, try/catch and handler label "kTimeCore.ToSmartLocalDate" stay verbatim, so error reports are unchanged.
    public static ToSmartLocalDate = (Self: kTimeCore, Locale: string = "", RecentDays: boolean = false): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (!Self.IsValid || Self.Year <= 1970) { return ""; }
            let UseLocale: string = Locale;
            if (UseLocale === "") { UseLocale = serverLocale(); }
            return kCoreTimeFormat.SmartLocalText(Self.constructor as typeof kTimeCore, Self.ToJsDate(), UseLocale, RecentDays);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.ToSmartLocalDate"); return ""; }
    };

    /**
     * The ToSmartLocalDate text of a JS Date (the body of ToSmartLocalDate and of the browser kTime override).
     * Locale "" = the runtime's default locale. RecentDays (opt-in, default off = unchanged output) adds two
     * steps for a message list: yesterday -> "yesterday 14:30" (the locale's word, Intl.RelativeTimeFormat),
     * 2-6 days ago -> the short weekday + time ("Mon 14:30"). Now is the reference moment (tests pass it).
     * "" for an invalid Date.
     */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.SmartLocalText - its gate, try/catch and handler label "kTimeCore.SmartLocalText" stay verbatim, so error reports are unchanged.
    public static SmartLocalText = (Self: typeof kTimeCore, When: Date, Locale: string = "", RecentDays: boolean = false, Now: Date = new Date()): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (isNaN(When.getTime()) === true) { return ""; }
            let UseLocale: string | undefined = undefined;
            if (Locale !== "") { UseLocale = Locale; }
            const Clock: string = When.toLocaleTimeString(UseLocale, { hour: "2-digit", minute: "2-digit" });
            const Days: number = kCoreTimeFormat.DayNumberOf(Self, Now) - kCoreTimeFormat.DayNumberOf(Self, When);
            if (Days === 0) { return Clock; }
            if (RecentDays === true && Days > 0 && Days < 7) { return kCoreTimeFormat.RecentDayText(Self, When, UseLocale, Days) + " " + Clock; }
            let DateText: string;
            if (When.getFullYear() === Now.getFullYear()) {
                DateText = When.toLocaleDateString(UseLocale, { month: "short", day: "numeric" });
            } else {
                DateText = When.toLocaleDateString(UseLocale, { year: "numeric", month: "short", day: "numeric" });
            }
            if (When.getHours() !== 0 || When.getMinutes() !== 0) { DateText = DateText + " " + Clock; }
            return DateText;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.SmartLocalText"); return ""; }
    };

    // "yesterday" (the locale's word) for Days 1, else the short weekday ("Mon").
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.RecentDayText - its gate, try/catch and handler label "kTimeCore.RecentDayText" stay verbatim, so error reports are unchanged.
    public static RecentDayText = (Self: typeof kTimeCore, When: Date, Locale: string | undefined, Days: number): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const Rtf: IRelativeTimeFormatCtor | undefined = (Intl as unknown as IIntlWithRelativeTime).RelativeTimeFormat;
            if (Days === 1 && Rtf !== undefined) { return new Rtf(Locale, { numeric: "auto" }).format(-1, "day"); }
            if (Days === 1) { return "yesterday"; }
            return When.toLocaleDateString(Locale, { weekday: "short" });
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.RecentDayText"); return ""; }
    };

    // The local calendar day as a whole number (DST-safe), for day differences.
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.DayNumberOf - its gate, try/catch and handler label "kTimeCore.DayNumberOf" stay verbatim, so error reports are unchanged.
    public static DayNumberOf = (Self: typeof kTimeCore, When: Date): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            return Math.round(Date.UTC(When.getFullYear(), When.getMonth(), When.getDate()) / 86400000);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.DayNumberOf"); return 0; }
    };

    /** "HH:mm" from the underlying JS Date's toTimeString(). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.To2DigitTime - its gate, try/catch and handler label "kTimeCore.To2DigitTime" stay verbatim, so error reports are unchanged.
    public static To2DigitTime = (Self: kTimeCore): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self.Year > 1969) { return Self.ToJsDate().toTimeString().substring(0, 5); }
            return "";
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.To2DigitTime"); return ""; }
    };
}
